import React from 'react';

import { Box, Typography } from '@mui/material';
import { Navigate, useNavigate } from 'react-router';

import { appRoutes } from '@clearverify/constants';
import type { SimpleObject } from '@clearverify/types';

import { InviteFrom, useAttestationsListQuery, useInviteQuery } from 'src/api';
import {
  PrepareFormDataType,
  prepareFormDataConsumer,
  prepareFormDataEmployerAdmin,
  prepareFormDataPEO,
  prepareFormDataPEOPayrollProvider,
  prepareFormDataUser,
  prepareFormDataVerifier,
} from 'src/components/auth/SignupByInvitePageForm/utils';
import { FullscreenLoader } from 'src/components/common';
import { IS_PRODUCTION_BUILD } from 'src/config';
import { AttestationPurpose } from 'src/constants/attestation';
import { SignupByInviteFormStepId } from 'src/constants/auth';
import { BusinessRole } from 'src/constants/user';
import { useInviteParamId } from 'src/hooks/invite';
import { useCurrentUserStatesList } from 'src/hooks/states';

import { AuthSignupContext, AuthSignupContextValue, FormStepperState } from './AuthSignupContext';

export const AuthSignupProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { data } = useAttestationsListQuery();

  const attestations = data?.attestationList;
  const navigate = useNavigate();

  const inviteId = useInviteParamId();

  const [formData, setFormData] = React.useState<SimpleObject>({});

  const [formFlow, setFormFlow] = React.useState<SignupByInviteFormStepId[]>([
    SignupByInviteFormStepId.initial,
  ]);

  const [prepareFormData, setPrepareFormData] = React.useState<PrepareFormDataType>(
    () => prepareFormDataUser,
  );

  const [currentStepIndex, setCurrentStepIndex] = React.useState(0);

  const formStepperState = React.useMemo((): FormStepperState => {
    const nextStepId = formFlow[currentStepIndex + 1];

    return {
      previousStepId: formFlow[currentStepIndex - 1],
      currentStepId: formFlow[currentStepIndex] as string,
      nextStepId,
      isNextStepAvailable: Boolean(nextStepId),
      isLastStep: currentStepIndex === formFlow.length - 1,
    };
  }, [currentStepIndex, formFlow]);

  const homeAddress = formData.homeAddress as
    | {
        state: string | undefined | null;
      }
    | undefined;

  const { isRequestAutoApproveStatusInactive } = useCurrentUserStatesList({
    state: homeAddress?.state,
  });

  const {
    data: inviteOriginalData,
    error,
    loading,
  } = useInviteQuery({
    variables: {
      id: inviteId ?? '',
    },
    skip: !inviteId,
  });

  React.useEffect(() => {
    if (IS_PRODUCTION_BUILD && inviteId && !loading && error) {
      navigate(appRoutes.auth.index);
    }
  }, [error, inviteId, loading, navigate]);

  const inviteData = React.useMemo(
    () => inviteOriginalData?.getInvite,
    [inviteOriginalData?.getInvite],
  );

  const goToNextStep = React.useCallback(() => {
    if (formStepperState.nextStepId) {
      setCurrentStepIndex(currentIndex => currentIndex + 1);
    }
  }, [formStepperState.nextStepId]);

  const goToPreviousStep = React.useCallback(() => {
    if (formStepperState.previousStepId) {
      setCurrentStepIndex(currentIndex => currentIndex - 1);
    }
  }, [formStepperState.previousStepId]);

  React.useEffect(() => {
    if (
      (inviteData && formFlow.length === 1,
      !formStepperState.nextStepId &&
        formStepperState.currentStepId === SignupByInviteFormStepId.initial)
    ) {
      // CV Staff form flow
      if (
        inviteData?.role === BusinessRole.PlatformManager ||
        inviteData?.role === BusinessRole.PlatformAdmin
      ) {
        const cvAttestations = attestations?.filter(attestation =>
          attestation.purpose?.includes(AttestationPurpose.forCvStaff),
        );

        setFormFlow(
          cvAttestations?.length
            ? cvFormFlow
            : cvFormFlow.filter(step => step !== SignupByInviteFormStepId.cvStaffAttestations),
        );
      }

      // Employer form flow
      const employerAttestations = attestations?.filter(attestation =>
        attestation.purpose?.includes(AttestationPurpose.forEmployers),
      );

      if (inviteData?.role === BusinessRole.EmployerManager) {
        setFormFlow(
          employerAttestations?.length
            ? employerManagerFormFlow
            : employerManagerFormFlow.filter(
                step => step !== SignupByInviteFormStepId.employeeAttestations,
              ),
        );
      }

      if (inviteData?.role === BusinessRole.EmployerAdmin) {
        let formFlow: SignupByInviteFormStepId[];

        if (inviteData.isEmployerUnderPeo && inviteData.from === InviteFrom.Platform) {
          formFlow = employerAdminUnderPeoInvitedCVFormFlow;

          if (inviteData.isBulkUploaded) {
            formFlow = employerAdminUnderPeoInvitedCVWithBulkUploadFormFlow;
          } else {
            formFlow = employerAdminUnderPeoInvitedCVFormFlow;
          }
        } else if (inviteData.isEmployerUnderPeo) {
          formFlow = employerAdminUnderPeoFormFlow;
        } else if (inviteData.isAdditionalStaff) {
          formFlow = employerAdditionalAdminFormFlow;
        } else {
          formFlow = employerAdminFormFlow;
        }
        if (!inviteData.isAdditionalStaff) {
          setPrepareFormData(() => prepareFormDataEmployerAdmin);
        }
        setFormFlow(
          employerAttestations?.length
            ? formFlow
            : formFlow.filter(step => step !== SignupByInviteFormStepId.employeeAttestations),
        );
      }

      // Verifier Form Flow
      const verifierAttestations = attestations?.filter(attestation =>
        attestation.purpose?.includes(AttestationPurpose.forVerifiers),
      );

      if (inviteData?.role === BusinessRole.VerifierManager) {
        setFormFlow(
          verifierAttestations?.length
            ? verifierManagerFormFlow
            : verifierManagerFormFlow.filter(
                step => step !== SignupByInviteFormStepId.verifierAttestations,
              ),
        );
      }

      if (inviteData?.role === BusinessRole.VerifierAdmin) {
        const formFlow = inviteData.isAdditionalStaff
          ? verifierAdditionalAdminFormFlow
          : verifierAdminFormFlow;

        if (!inviteData.isAdditionalStaff) {
          setPrepareFormData(() => prepareFormDataVerifier);
        }
        setFormFlow(
          verifierAttestations?.length
            ? formFlow
            : formFlow.filter(step => step !== SignupByInviteFormStepId.verifierAttestations),
        );
      }

      // Consumer form flow
      if (inviteData?.role === BusinessRole.Employee) {
        const consumerAttestations = attestations?.filter(attestation =>
          attestation.purpose?.includes(AttestationPurpose.forConsumers),
        );

        const formFlowAttestations = consumerAttestations?.length
          ? consumerFormFlow
          : consumerFormFlow.filter(step => step !== SignupByInviteFormStepId.consumerAttestations);

        const formFlow = isRequestAutoApproveStatusInactive
          ? formFlowAttestations.filter(
              step => step !== SignupByInviteFormStepId.consumerVerificationPreference,
            )
          : formFlowAttestations;

        setPrepareFormData(() => prepareFormDataConsumer);
        setFormFlow(formFlow);
      }

      // PEO form flow
      const peoAttestations = attestations?.filter(attestation =>
        attestation.purpose?.includes(AttestationPurpose.forPeo),
      );

      if (
        inviteData?.role === BusinessRole.PeoSuperAdmin ||
        inviteData?.role === BusinessRole.PeoAdmin
      ) {
        let formFlow = inviteData.isAdditionalStaff ? peoAdditionalStaffFormFlow : peoAdminFormFlow;

        if (inviteData.isBulkUploaded) {
          formFlow = peoAdditionalStaffFormFlow;
        }

        if (!inviteData.isAdditionalStaff) {
          setPrepareFormData(() => prepareFormDataPEO);
        }
        setFormFlow(
          peoAttestations?.length
            ? formFlow
            : formFlow.filter(step => step !== SignupByInviteFormStepId.peoAttestations),
        );
      }

      if (inviteData?.role === BusinessRole.PeoManager) {
        setFormFlow(
          peoAttestations?.length
            ? peoAdditionalStaffFormFlow
            : peoAdditionalStaffFormFlow.filter(
                step => step !== SignupByInviteFormStepId.peoAttestations,
              ),
        );
      }

      // PEO PP form flow
      if (inviteData?.role === BusinessRole.PeoPayrollProviderUser) {
        const peoPpAttestations = attestations?.filter(attestation =>
          attestation.purpose?.includes(AttestationPurpose.forPeoPp),
        );

        const formFlow = inviteData.isAdditionalStaff
          ? peoPayrollProviderAdditionalStaffFormFlow
          : peoPayrollProviderStaffFormFlow;

        if (!inviteData.isAdditionalStaff) {
          setPrepareFormData(() => prepareFormDataPEOPayrollProvider);
        }
        setFormFlow(
          peoPpAttestations?.length
            ? formFlow
            : formFlow.filter(
                step => step !== SignupByInviteFormStepId.peoPayrollProviderAttestations,
              ),
        );
      }
    }
    if (
      inviteData &&
      formStepperState.currentStepId === SignupByInviteFormStepId.consumerAttestations &&
      inviteData.role === BusinessRole.Employee
    ) {
      setFormFlow(prevState =>
        isRequestAutoApproveStatusInactive
          ? prevState.filter(
              step => step !== SignupByInviteFormStepId.consumerVerificationPreference,
            )
          : consumerFormFlow,
      );
    }
  }, [
    attestations,
    formFlow.length,
    formStepperState.currentStepId,
    formStepperState.nextStepId,
    inviteData,
    isRequestAutoApproveStatusInactive,
  ]);

  const updateFormData = React.useCallback((formValues: SimpleObject) => {
    setFormData(currentFormData => {
      return { ...currentFormData, ...formValues };
    });
  }, []);

  const contextValue: AuthSignupContextValue = React.useMemo(() => {
    return {
      formData,
      setFormData,
      updateFormData,
      prepareFormData,
      formStepperState,
      goToNextStep,
      goToPreviousStep,
      inviteData,
    };
  }, [
    formData,
    formStepperState,
    goToNextStep,
    goToPreviousStep,
    inviteData,
    prepareFormData,
    updateFormData,
  ]);

  if (!inviteId) {
    return <Navigate to={appRoutes.auth.index} />;
  }

  if (loading) {
    return <FullscreenLoader sx={{ height: 400 }} />;
  }

  if (error && error.message !== 'Invite not found or expired') {
    return (
      <Box p={5} textAlign="center">
        <Typography variant="body1">{error.message}</Typography>
      </Box>
    );
  }

  return <AuthSignupContext.Provider value={contextValue}>{children}</AuthSignupContext.Provider>;
};

const cvFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.cvStaffAttestations,
  SignupByInviteFormStepId.noticeToFurnishers,
];
const employerManagerFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.employeeAttestations,
  SignupByInviteFormStepId.noticeToFurnishers,
];
const verifierManagerFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.verifierAttestations,
  SignupByInviteFormStepId.noticeToFurnishers,
];
const verifierAdminFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.choseVerifierAccountType,
  SignupByInviteFormStepId.verifierBasicInfo,
  SignupByInviteFormStepId.noticeToFurnishers,
  SignupByInviteFormStepId.yourCompanyExistInfoBlock,
];

const verifierAdditionalAdminFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.verifierAttestations,
  SignupByInviteFormStepId.noticeToFurnishers,
];

const employerAdminFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.employerBasicInfo,
  SignupByInviteFormStepId.employeeSignupMethod,
  SignupByInviteFormStepId.employeeAttestations,
  SignupByInviteFormStepId.noticeToFurnishers,
  SignupByInviteFormStepId.yourCompanyExistInfoBlock,
];

const employerAdminUnderPeoFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.employeeAttestations,
  SignupByInviteFormStepId.noticeToFurnishers,
];

const employerAdminUnderPeoInvitedCVFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.employerBasicInfo,
  SignupByInviteFormStepId.employeeAttestations,
  SignupByInviteFormStepId.noticeToFurnishers,
  SignupByInviteFormStepId.yourCompanyExistInfoBlock,
];

const employerAdminUnderPeoInvitedCVWithBulkUploadFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.employerBasicInfo,
  SignupByInviteFormStepId.employeeAttestations,
  SignupByInviteFormStepId.noticeToFurnishers,
  SignupByInviteFormStepId.yourCompanyExistInfoBlock,
];

const employerAdditionalAdminFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.employeeAttestations,
  SignupByInviteFormStepId.noticeToFurnishers,
];

const consumerFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.consumerAttestations,
  SignupByInviteFormStepId.consumerVerificationPreference,
  SignupByInviteFormStepId.consumerReviewData,
];

const peoAdminFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.peoBasicInfo,
  SignupByInviteFormStepId.employeeVerificationPreference,
  SignupByInviteFormStepId.peoAttestations,
  SignupByInviteFormStepId.noticeToFurnishers,
  SignupByInviteFormStepId.yourCompanyExistInfoBlock,
];

const peoAdditionalStaffFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.peoAttestations,
  SignupByInviteFormStepId.noticeToFurnishers,
];

const peoPayrollProviderStaffFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.peoPayrollProviderBasicInfo,
  SignupByInviteFormStepId.employeeVerificationPreference,
  SignupByInviteFormStepId.peoPayrollProviderAttestations,
  SignupByInviteFormStepId.noticeToFurnishers,
  SignupByInviteFormStepId.yourCompanyExistInfoBlock,
];

const peoPayrollProviderAdditionalStaffFormFlow = [
  SignupByInviteFormStepId.initial,
  SignupByInviteFormStepId.peoPayrollProviderAttestations,
  SignupByInviteFormStepId.noticeToFurnishers,
];

AuthSignupProvider.displayName = 'AuthSignupProvider';
